import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import { ILoyaltyCustomerExtended } from "pages/secure/facility/settings/loyaltyProgram/loyaltyTabs/LoyaltyCustomers";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import "./redeemPointsModal.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp, faStar } from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import { IReward } from "redux/reducers/models/loyalty";
import { GetLoyaltyProgramRewards } from "api/rpc/2024-04/facilityAdmin/customer/loyalty";
import axios, { CancelToken } from "axios";
import { StatusCode } from "api/protocols";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { showError } from "redux/actions/ui";
import Spin from "components/spin/spin";
import { IProduct, TProductType } from "redux/reducers/models/product";
import Checkbox from "components/form/checkbox/Checkbox";
import { LocaleCurrency } from "helpers/Locale";
import { ICartLineItem, IDiscountLines } from "redux/reducers/models/cart";
import classNames from "classnames";


interface IProps {
    open: boolean;
    customer: ILoyaltyCustomerExtended & {
        points_available?: number;
    };
    onCancel: () => void;
    onOk: (rewards: Array<{reward_id: number; cart_line_item_id: number}>) => void;
    lineItems: Array<ICartLineItem>;
    discountLines: Array<IDiscountLines>
}

interface IRewardExtended extends IReward {
    products?: Array<IProduct>; 
    product_types?: Array<TProductType>;
    open?: boolean; 
    lineItems?: Array<ICartLineItem>
}

interface IState {
    rewards: Array<IRewardExtended>;
    selectedRewards: Array<{reward_id: number; line_item_id: number; product_title: string; variant_title: string; points: number}>;
}

//Points based redemption
export default function RedeemPointsModal(props: IProps) {
    const {open, onCancel, customer, lineItems, discountLines, onOk} = props;
    const {t} = useTranslation();
    const dispatch = useAppDispatch();
    const {facilityStore} = useAppSelector(store => store);
    const [state, setState] = useState<IState>({
        rewards: undefined,
        selectedRewards: [],
    });

    const [pointsToUse, setPointsToUse] = useState<number>(0);

    useEffect(() => {
        const source = axios.CancelToken.source();
        if (open) {
            void getRewards(source.token);
        } else {
            setState(prev => ({...prev, rewards: undefined, selectedRewards: []}));
        }
        return () => source.cancel();
    }, [open]);

    useEffect(() => {
        const totalPoints = state?.selectedRewards?.reduce((total, curr) => total + curr?.points, 0);
        setPointsToUse(totalPoints);
    }, [state?.selectedRewards]);

    async function getRewards(cancelToken: CancelToken) {
        if (state?.rewards !== undefined) {
            setState(prev => ({...prev, rewards: undefined}));
        }
        const res = await GetLoyaltyProgramRewards(false, cancelToken);
        if (res?.status !== StatusCode.OK) {
            if (cancelToken && cancelToken?.reason) {
                return;
            }
            dispatch(showError(t("Error getting rewards")));
            return;
        }
        const rewards: Array<IRewardExtended> = res?.data;
        const updatedRewards = rewards?.reduce((rewardsLineItems, reward) => {
            //Don't use tier based rewards for point based program
            if (reward?.loyalty_program_tier_id) {
                return rewardsLineItems;
            }
            
            if (reward?.application === "product") {
                const filteredLineItems = lineItems?.filter(lineItem => reward?.products?.some(product => product?.id === lineItem?.product_id));
                //No eligible rewards, don't display reward
                if (filteredLineItems?.length === 0) {
                    return rewardsLineItems;
                }
                return [...rewardsLineItems, {...reward, lineItems: filteredLineItems, open: false}];
            } else if (reward?.application === "product_categories") {
                const filteredLineItems = lineItems?.filter(lineItem => reward?.product_types?.some(type => type?.title === lineItem?.product?.type));
                //No eligible rewards, don't display reward
                if (filteredLineItems?.length === 0) {
                    return rewardsLineItems;
                }
                return [...rewardsLineItems, {...reward, lineItems: filteredLineItems, open: false}];
            }
            
            return [...rewardsLineItems, {...reward, lineItems: [], open: false}];
        } ,[] as Array<IRewardExtended>);
        setState(prev => ({...prev, rewards: updatedRewards}));
    }

    function handleSelectReward(rewardId: number, points: number, lineItem?: ICartLineItem) {
        let updatedSelection = [...state.selectedRewards];
        const foundReward = updatedSelection?.findIndex(reward => lineItem ? lineItem?.id === reward?.line_item_id : rewardId === reward?.reward_id);
        if (foundReward !== -1) {
            updatedSelection = updatedSelection?.filter(reward => lineItem ? lineItem?.id !== reward?.line_item_id : rewardId !== reward?.reward_id);
        } else {
            updatedSelection = [...updatedSelection, {
                reward_id: rewardId, 
                line_item_id: lineItem?.id, 
                product_title: lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title, 
                variant_title: lineItem?.variant_title !== lineItem?.product?.title ? lineItem?.variant_title : "",
                points: points,
            }];
        }
        setState(prev => ({...prev, selectedRewards: updatedSelection}));
    }

    function handleOk() {
        const rewardLineItems = state?.selectedRewards?.map(reward => ({reward_id: reward?.reward_id, cart_line_item_id: reward?.line_item_id}));
        if (rewardLineItems?.length > 0) {
            void onOk(rewardLineItems);
        }
    }

    return (
        <Portal isMounted={open}>
            <Sheet
                title={t("Redeem Points")}
                supportText={t("Choose one method to redeem points on this order")}
                open={open}
                onCancel={() => onCancel()}
                onOk={handleOk}
                okDisabled={state?.selectedRewards?.length === 0}
                size="small"
                okText={t("Continue")}
                secondaryFooter={
                    <p className="redeem-points-modal-rewards-title">{t("Selected")}: {state?.selectedRewards?.length} {t("items")} • {pointsToUse} {t("pts ")} 
                    — {t(" Remaining:")} {customer?.points_available - pointsToUse} / {customer?.points_balance}
                    </p>
                }
            >
            <div className="redeem-points-modal-rewards-container">
                {state?.rewards === undefined ? (<div style={{width: "100%", height: "30px", display: "flex", justifyContent: "center"}}><Spin/></div>) : (
                    <>
                    {state?.rewards?.map((reward, index) => {
                        const hasRewards = reward?.product_types?.length > 0 || reward?.products?.length > 0;
                        const amountOfRewards = reward?.lineItems?.filter(lineItem => !discountLines?.some(discount => discount?.cart_line_item_id === lineItem?.id))?.length;
                        const validRewards = amountOfRewards > 0;
                        if (reward?.application === "product" || reward?.application === "product_categories") {
                            if (validRewards) {
                            return (
                                //Reward w/ products or product types
                                <div key={index}>
                                <div className="redeem-points-modal-reward-container" 
                                onClick={hasRewards ? () => setState(prev => ({...prev, rewards: prev?.rewards?.map(prevReward => prevReward?.id === reward?.id ? {...prevReward, open: !prevReward?.open} : prevReward)})) : undefined}>
                                    <div style={{display: "flex", gap: "1rem"}}>
                                    <div className="pointer-events-none">
                                        <Checkbox labelHidden size="medium" checked={state?.selectedRewards?.some(foundReward => foundReward?.reward_id === reward?.id)} onChange={() => handleSelectReward(reward?.id, reward?.points_required)}/>
                                    </div>
                                    <div>
                                    <p className="redeem-points-modal-rewards-title">{reward?.name} <span className="ml-2">

                                        {hasRewards ? reward?.application === "product" ? <Badge type="gray" size="small">{amountOfRewards}</Badge> : reward?.application === "product_categories" ? 
                                        <Badge type="gray" size="small">{amountOfRewards}</Badge> : null : null}</span></p>
                                         <p className="redeem-points-modal-subtitle">{reward?.points_required} {t("points")} = {reward?.reward_type === "discount_amount" ? 
                                        <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={reward?.discount_value}/> : 
                                        `${reward?.discount_value}%`
                                        } {t("off")}</p>
                                         <p className="redeem-points-modal-subtitle redeem-points-modal-selected-product-container">{state?.selectedRewards?.filter(selectedReward => selectedReward?.reward_id === reward?.id)?.map((selectedReward, selectedIndex) => (
                                        <span key={selectedIndex} className="redeem-points-modal-selected-product">{selectedReward?.product_title} {selectedReward?.variant_title ? ` - ${selectedReward?.variant_title}` : null} </span>
                                    ))}</p>
                                    </div>
                                    </div>
                                    {hasRewards ? (
                                        <FontAwesomeIcon icon={reward?.open === false ? faChevronDown : faChevronUp} />
                                    ) : null}
                                </div>
                                    {reward?.open ? (
                                        <div className="redeem-points-modal-product-outer">
                                        {reward?.application === "product" ? reward?.lineItems?.filter(lineItem => !discountLines?.some(discount => discount?.cart_line_item_id === lineItem?.id))?.map((lineItem, lineItemIndex) => {
                                            const checked = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id === reward?.id);
                                            const disabled = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id !== reward?.id) 
                                            || ((((pointsToUse + reward?.points_required) > customer?.points_balance) || (customer?.points_available - pointsToUse < reward?.points_required)) && !checked);
                                            //Specific product rewards
                                            return (
                                                <div className={classNames("redeem-points-modal-product-container", {
                                                    "redeem-points-modal-product-container-disabled": disabled,
                                                })} key={lineItemIndex} onClick={!disabled ? () => handleSelectReward(reward?.id, reward?.points_required, lineItem) : undefined}>
                                                <div style={{display: "flex", gap: "1rem"}}>
                                                    <div className="pointer-events-none">
                                                        <Checkbox 
                                                        labelHidden 
                                                        size="medium" 
                                                        checked={checked} 
                                                        disabled={disabled}
                                                        onChange={() => handleSelectReward(reward?.id, reward?.points_required, lineItem)}/>
                                                    </div>
                                                    <div>
                                                    <p className="redeem-points-modal-rewards-title">{lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title}</p>
                                                    {lineItem?.product?.title !== lineItem?.variant_title ? (
                                                        <p className="redeem-points-modal-subtitle">{lineItem?.variant_title}</p>
                                                    ) : null}
                                                    </div>
                                                </div>
                                                <Badge size="small" colour="#363F72" backgroundColour="#F8F9FC">{reward?.points_required} {t("pts")} <FontAwesomeIcon icon={faStar}/></Badge>
                                            </div>
                                            );
                                        }) : 
                                        reward?.lineItems?.filter(lineItem => !discountLines?.some(discount => discount?.cart_line_item_id === lineItem?.id))?.map((lineItem, lineItemIndex) => {
                                            const checked = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id === reward?.id);
                                            const disabled = state?.selectedRewards?.some(foundReward => foundReward?.line_item_id === lineItem?.id && foundReward?.reward_id !== reward?.id) 
                                            || ((((pointsToUse + reward?.points_required) > customer?.points_balance) || (customer?.points_available - pointsToUse < reward?.points_required)) && !checked);
                                            //Product categories reward
                                            return (
                                                <div className={classNames("redeem-points-modal-product-container", {
                                                    "redeem-points-modal-product-container-disabled": disabled,
                                                })} key={lineItemIndex} onClick={!disabled ? () => handleSelectReward(reward?.id, reward?.points_required, lineItem) : undefined}>
                                                <div style={{display: "flex", gap: "1rem"}}>
                                                    <div className="pointer-events-none">
                                                        <Checkbox 
                                                        labelHidden 
                                                        size="medium" 
                                                        checked={checked} 
                                                        disabled={disabled}
                                                        onChange={() => handleSelectReward(reward?.id, reward?.points_required, lineItem)}/>
                                                    </div>
                                                    <div>
                                                    <p className="redeem-points-modal-rewards-title">{lineItem?.preferred_title ? lineItem?.preferred_title : lineItem?.product?.title}</p>
                                                    {lineItem?.product?.title !== lineItem?.variant_title ? (
                                                        <p className="redeem-points-modal-subtitle">{lineItem?.variant_title}</p>
                                                    ) : null}
                                                    </div>
                                                </div>
                                                <Badge size="small" colour="#363F72" backgroundColour="#F8F9FC">{reward?.points_required} {t("pts")} <FontAwesomeIcon icon={faStar}/></Badge>
                                            </div>
                                            );
                                        })
                                        }
                                        </div>
                                    ) : null}
                                </div>
                            );
                        }
                        } else {
                            const validOrderReward = !discountLines?.some(discount => discount?.loyalty_points_reward_id === reward?.id);
                            if (validOrderReward) {
                            const checked = state?.selectedRewards?.some(foundReward => foundReward?.reward_id === reward?.id);
                            const disabled = ((pointsToUse + reward?.points_required) > customer?.points_balance || (customer?.points_available - pointsToUse < reward?.points_required)) && !checked;
                            //Order subtotal reward
                            return (
                                <div key={index} className="redeem-points-modal-reward-container" onClick={!disabled ? () => handleSelectReward(reward?.id, reward?.points_required) : undefined}>
                                    <div style={{display: "flex", gap: "1rem"}}>
                                        <div className="pointer-events-none">
                                            <Checkbox disabled={disabled} labelHidden size="medium" checked={checked} onChange={() => handleSelectReward(reward?.id, reward?.points_required)}/>
                                        </div>
                                        <div>
                                    <p className="redeem-points-modal-rewards-title">{reward?.name}</p>
                                    <p className="redeem-points-modal-subtitle">{reward?.points_required} {t("points")} = {reward?.reward_type === "discount_amount" ? 
                                        <LocaleCurrency currency={facilityStore?.facility?.default_currency_code} amount={reward?.discount_value}/> : 
                                        `${reward?.discount_value}%`
                                        } {t("off subtotal")}</p>
                                        </div>
                                    </div>
                                    <Badge size="small" colour="#363F72" backgroundColour="#F8F9FC">{reward?.points_required} {t("pts")} <FontAwesomeIcon icon={faStar}/></Badge>
                                </div>
                            );
                            }
                            
                        }
                    })}
                    </>
                )}
            </div>
            </Sheet>
        </Portal>
    );
}